import * as React from 'react';

import { cn } from './utils';

const Card = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(({ className, ...props }, ref) => (
  <div
    data-slot="card"
    ref={ref}
    className={cn(
      // Light-mode shadows bumped 2026-05-11: 0.04→0.06 (close shadow) and
      // 0.03→0.05 (ambient shadow), with the ambient shadow's spread bumped
      // 20px→24px. ~50% more visible elevation against the slightly darker
      // --background (94.5% lightness vs the previous 96.5%). Dark mode
      // shadow stays disabled.
      'bg-card text-card-foreground flex flex-col gap-6 rounded-xl border border-border/70 shadow-[0_1px_2px_rgba(15,23,42,0.06),0_8px_24px_rgba(15,23,42,0.05)] dark:border-border dark:shadow-none focus-within:border-ring focus-within:ring-ring/40 focus-within:ring-[3px]',
      className,
    )}
    {...props}
  />
));
Card.displayName = 'Card';

const CardHeader = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(({ className, ...props }, ref) => (
  <div
    data-slot="card-header"
    ref={ref}
    className={cn(
      '@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 pt-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6',
      className,
    )}
    {...props}
  />
));
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<HTMLHeadingElement, React.ComponentProps<'h4'>>(({ className, ...props }, ref) => (
  <h4 data-slot="card-title" ref={ref} className={cn('leading-none', className)} {...props} />
));
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<HTMLParagraphElement, React.ComponentProps<'p'>>(
  ({ className, ...props }, ref) => (
    <p data-slot="card-description" ref={ref} className={cn('text-muted-foreground', className)} {...props} />
  ),
);
CardDescription.displayName = 'CardDescription';

const CardAction = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(({ className, ...props }, ref) => (
  <div
    data-slot="card-action"
    ref={ref}
    className={cn('col-start-2 row-span-2 row-start-1 self-start justify-self-end', className)}
    {...props}
  />
));
CardAction.displayName = 'CardAction';

const CardContent = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(({ className, ...props }, ref) => (
  <div data-slot="card-content" ref={ref} className={cn('px-6 [&:last-child]:pb-6', className)} {...props} />
));
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(({ className, ...props }, ref) => (
  <div
    data-slot="card-footer"
    ref={ref}
    className={cn('flex items-center px-6 pb-6 [.border-t]:pt-6', className)}
    {...props}
  />
));
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardFooter, CardTitle, CardAction, CardDescription, CardContent };
